'use client'

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { usePathname } from 'next/navigation'

// ============================================================================
// Chat message types
// ============================================================================

export interface ToolCall {
  id: string
  name: string
  args: Record<string, unknown>
  result?: unknown
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  isLoading?: boolean
  toolCalls?: ToolCall[]
}

// ============================================================================
// Policy / Insight types (unchanged — used by /ai/policies and /ai/insights)
// ============================================================================

export interface AIPolicy {
  id: string
  name: string
  description: string
  naturalLanguage: string
  dsl: PolicyDSL | null
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface PolicyDSL {
  conditions: PolicyCondition[]
  actions: PolicyAction[]
  match_mode?: 'all' | 'any'
  stop_on_match?: boolean
}

export interface PolicyCondition {
  field: string
  operator: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with' | 'between' | 'in' | 'not_in' | 'matches' | 'is_null' | 'is_not_null'
  value: string | number | boolean | unknown[] | null
  field_path?: string
}

export interface PolicyAction {
  type: 'auto_approve' | 'auto_reject' | 'flag_review' | 'notify' | 'set_status' | 'set_field' | 'add_note' | 'add_tag' | 'escalate' | 'custom'
  value?: string | number | boolean | Record<string, unknown>
  params?: Record<string, unknown>
}

export interface AIInsight {
  id: string
  type: 'pattern' | 'anomaly' | 'recommendation'
  severity: 'critical' | 'warning' | 'info'
  title: string
  description: string
  data?: Record<string, unknown>
  suggestedAction?: string
  createdAt: Date
}

// ============================================================================
// Context state type
// ============================================================================

interface AIContextState {
  // AI Manager visibility
  isManagerOpen: boolean
  openManager: () => void
  closeManager: () => void
  toggleManager: () => void

  // Chat state
  chatHistory: ChatMessage[]
  /** Add a message; automatically removes any isLoading placeholder for assistant messages */
  addMessage: (msg: Omit<ChatMessage, 'id' | 'timestamp'>) => void
  /** In-place patch of the last assistant message — used while waiting for API response */
  updateLastAssistantMessage: (patch: Partial<Omit<ChatMessage, 'id' | 'role' | 'timestamp'>>) => void
  clearHistory: () => void
  isTyping: boolean
  setIsTyping: (typing: boolean) => void

  // Page context (auto-derived from router)
  currentPageContext: string

  // Policies state
  policies: AIPolicy[]
  setPolicies: (policies: AIPolicy[]) => void
  addPolicy: (policy: AIPolicy) => void
  updatePolicy: (id: string, updates: Partial<AIPolicy>) => void
  removePolicy: (id: string) => void

  // Insights state
  insights: AIInsight[]
  setInsights: (insights: AIInsight[]) => void
  pendingInsightsCount: number
  markInsightAsRead: (id: string) => void

  // Suggestions indicator
  hasPendingSuggestions: boolean
  setHasPendingSuggestions: (has: boolean) => void
}

const AIContext = createContext<AIContextState | undefined>(undefined)

export function useAI() {
  const context = useContext(AIContext)
  if (!context) {
    throw new Error('useAI must be used within an AIProvider')
  }
  return context
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

// ============================================================================
// Provider
// ============================================================================

export function AIProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()

  // Manager visibility
  const [isManagerOpen, setIsManagerOpen] = useState(false)

  // Chat
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([])
  const [isTyping, setIsTyping] = useState(false)

  // Policies
  const [policies, setPolicies] = useState<AIPolicy[]>([])

  // Insights
  const [insights, setInsights] = useState<AIInsight[]>([])
  const [readInsightIds, setReadInsightIds] = useState<Set<string>>(new Set())

  // Suggestions indicator
  const [hasPendingSuggestions, setHasPendingSuggestions] = useState(false)

  // Derive page context from pathname
  const currentPageContext = pathname || '/'

  // Manager controls
  const openManager = useCallback(() => setIsManagerOpen(true), [])
  const closeManager = useCallback(() => setIsManagerOpen(false), [])
  const toggleManager = useCallback(() => setIsManagerOpen(prev => !prev), [])

  // ---- Chat controls -------------------------------------------------------

  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: generateId(),
      timestamp: new Date(),
    }
    // When a real assistant message arrives, remove any isLoading placeholder
    if (message.role === 'assistant' && !message.isLoading) {
      setChatHistory(prev => [...prev.filter(m => !m.isLoading), newMessage])
    } else {
      setChatHistory(prev => [...prev, newMessage])
    }
  }, [])

  const updateLastAssistantMessage = useCallback(
    (patch: Partial<Omit<ChatMessage, 'id' | 'role' | 'timestamp'>>) => {
      setChatHistory(prev => {
        const idx = [...prev].reverse().findIndex(m => m.role === 'assistant')
        if (idx === -1) return prev
        const realIdx = prev.length - 1 - idx
        const updated = [...prev]
        updated[realIdx] = { ...updated[realIdx], ...patch }
        return updated
      })
    },
    [],
  )

  const clearHistory = useCallback(() => {
    setChatHistory([])
  }, [])

  // ---- Policy controls -----------------------------------------------------

  const addPolicy = useCallback((policy: AIPolicy) => {
    setPolicies(prev => [...prev, policy])
  }, [])

  const updatePolicy = useCallback((id: string, updates: Partial<AIPolicy>) => {
    setPolicies(prev =>
      prev.map(p => (p.id === id ? { ...p, ...updates, updatedAt: new Date() } : p)),
    )
  }, [])

  const removePolicy = useCallback((id: string) => {
    setPolicies(prev => prev.filter(p => p.id !== id))
  }, [])

  // ---- Insights controls ---------------------------------------------------

  const pendingInsightsCount = insights.filter(i => !readInsightIds.has(i.id)).length

  const markInsightAsRead = useCallback((id: string) => {
    setReadInsightIds(prev => new Set([...prev, id]))
  }, [])

  // ---- Global keyboard shortcut (Cmd+J / Ctrl+J) --------------------------

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        toggleManager()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggleManager])

  // -------------------------------------------------------------------------

  const value: AIContextState = {
    isManagerOpen,
    openManager,
    closeManager,
    toggleManager,

    chatHistory,
    addMessage,
    updateLastAssistantMessage,
    clearHistory,
    isTyping,
    setIsTyping,

    currentPageContext,

    policies,
    setPolicies,
    addPolicy,
    updatePolicy,
    removePolicy,

    insights,
    setInsights,
    pendingInsightsCount,
    markInsightAsRead,

    hasPendingSuggestions,
    setHasPendingSuggestions,
  }

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>
}
